import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterColumn, SavedView } from './types';
import { useListSearchParams } from './useListSearchParams';
import { ViewGroup, ViewSort } from './viewDefinition';
import { applyClientFilters } from './applyClientFilters';
import { resolveViewQuery } from './viewQuery';
import { sortItems } from './sortItems';
import { tallyLookup, withoutColumn } from './facetCounts';

// Orchestration for a CRM-style client-side list (spec 2026-07-22): binds the URL codec
// (useListSearchParams) + quick-view pills + the applyClientFilters engine + search + sort into one
// hook the Reviews / Action Items / Clients pages share. Data path stays fetch-all-then-filter.

const SEARCH_PARAM = 'q';

/** Filter-only subset of a record (drops `view`/other keys a view query might carry). */
function filterKeysOnly(rec: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec)) if (k.startsWith('filter.')) out[k] = v;
  return out;
}

function shallowEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => b[k] === a[k]);
}

export interface UseListViewOptions<T> {
  /**
   * Key of the canonical default view. Its `view=` param is omitted from the URL, and its query is
   * APPLIED, not written, whenever the URL carries no `view` and no `filter.*` (v2 §11 RULE V3 —
   * mount writes nothing). May change after mount (e.g. once the caller's identity resolves) without
   * any URL write; an explicit URL always wins over it (RULE V4).
   */
  defaultViewKey: string;
  /** Quick-view presets. Each `query` holds `filter.<col>.<op>` keys (may use `{{tokens}}`). */
  views: SavedView[];
  /** Filterable/sortable column catalog (drives the engine + the header controls). */
  columns: FilterColumn<T>[];
  data: T[] | undefined;
  /** Accessors searched by the free-text box (case-insensitive substring, OR across accessors). */
  searchAccessors?: ((item: T) => string | null | undefined)[];
  /** Injectable clock for dynamic date tokens (tests). */
  now?: Date;
}

export interface UseListViewResult<T> {
  /**
   * The EFFECTIVE filters: the URL's, or — on a URL with no `view` and no `filter.*` — the default
   * view's, resolved. Chips and header controls read this, so the defaults are visible and removable.
   */
  filters: Record<string, string>;
  setFilters: (next: Record<string, string>) => void;
  sort: ViewSort | null;
  setSort: (sort: ViewSort | null) => void;
  search: string;
  setSearch: (q: string) => void;
  /** The grouped column from the URL, or null (v2 §11 V1). */
  group: ViewGroup | null;
  setGroup: (group: ViewGroup | null) => void;
  /** The pill to highlight — the default view when no filters, else the matching preset (or ''). */
  activeViewKey: string;
  /** Select a quick view: expands its filters into the URL and marks `view=` (default omits it). */
  selectView: (key: string) => void;
  /** Clear all filters + search back to the default view. */
  clearAll: () => void;
  processedData: T[];
  totalCount: number;
  filteredCount: number;
  /**
   * C86 / §16.3 facets for a lookup column: rows matching every active filter EXCEPT this column's own, the search
   * included, tallied by value, with "(has no value)" under NO_VALUE_TOKEN. A function, called only while a filter
   * panel is open, so nothing is counted that nobody is looking at. `{}` for a column that is not a lookup.
   */
  facetCountsFor: (columnKey: string) => Record<string, number>;
  /**
   * A lookup column's options: its own `filterOptions` when it declares them; otherwise (a staff or other open-ended
   * lookup) every distinct value in the WHOLE loaded list, by label, so an option whose count drops to 0 under other
   * filters still shows (dimmed), as C86 requires, instead of vanishing.
   */
  lookupOptionsFor: (columnKey: string) => { value: string; label: string }[];
}

export function useListView<T>({
  defaultViewKey,
  views,
  columns,
  data,
  searchAccessors = [],
  now,
}: UseListViewOptions<T>): UseListViewResult<T> {
  const list = useListSearchParams(defaultViewKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(SEARCH_PARAM) ?? '';

  const setSearch = useCallback(
    (q: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (q) next.set(SEARCH_PARAM, q);
          else next.delete(SEARCH_PARAM);
          next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectView = useCallback(
    (key: string) => {
      const view = views.find((v) => v.key === key);
      const expanded = view ? filterKeysOnly(resolveViewQuery(view.query, now)) : {};
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const k of [...next.keys()]) if (k.startsWith('filter.')) next.delete(k);
          next.delete('page');
          if (key === defaultViewKey) next.delete('view');
          else next.set('view', key);
          for (const [k, v] of Object.entries(expanded)) next.set(k, v);
          return next;
        },
        { replace: true }
      );
    },
    [views, defaultViewKey, now, setSearchParams]
  );

  const clearAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of [...next.keys()]) {
          if (k.startsWith('filter.') || k === SEARCH_PARAM || k === 'view' || k === 'page') next.delete(k);
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // RULE V3 (fleet contract v2 §11): a bare URL means "the default view", and the default view's
  // query is APPLIED here rather than stamped into the URL. Stamping it would make C41 pass for the
  // wrong reason (restoring defaults looks like restoring state) and fail C43 outright. The moment
  // the URL carries a `view` or any `filter.*`, the URL alone decides (RULE V4: a default never
  // overrides a restored view — including one that arrives late, like the caller's identity).
  const urlIsBare = list.rawView === null && Object.keys(list.filters).length === 0;
  const defaultView = views.find((v) => v.key === defaultViewKey);
  const filters = useMemo(
    () => (urlIsBare && defaultView ? filterKeysOnly(resolveViewQuery(defaultView.query, now)) : list.filters),
    [urlIsBare, defaultView, list.filters, now]
  );

  // The pill to light: the default view on a bare URL; the explicitly named view while its own
  // filters are untouched; otherwise the preset whose expanded filters match exactly (so manual
  // header edits still light up a matching pill), else none.
  const activeViewKey = useMemo(() => {
    if (urlIsBare) return defaultViewKey;
    const current = filterKeysOnly(filters);
    const named = list.rawView ? views.find((v) => v.key === list.rawView) : undefined;
    if (named && shallowEqual(filterKeysOnly(resolveViewQuery(named.query, now)), current)) return named.key;
    if (Object.keys(current).length === 0) {
      const empty = views.find((v) => Object.keys(filterKeysOnly(v.query)).length === 0);
      return empty?.key ?? defaultViewKey;
    }
    const match = views.find((v) => shallowEqual(filterKeysOnly(resolveViewQuery(v.query, now)), current));
    return match?.key ?? '';
  }, [urlIsBare, filters, list.rawView, views, defaultViewKey, now]);

  const matchesSearch = useCallback(
    (rows: T[]): T[] => {
      const q = search.trim().toLowerCase();
      return q
        ? rows.filter((item) =>
            searchAccessors.some((acc) => {
              const val = acc(item);
              return val != null && String(val).toLowerCase().includes(q);
            })
          )
        : rows;
    },
    [search, searchAccessors]
  );

  const processedData = useMemo(() => {
    const base = data ?? [];
    const filtered = applyClientFilters(base, filters, columns, now);
    return sortItems(matchesSearch(filtered), list.sort, columns);
  }, [data, filters, columns, now, matchesSearch, list.sort]);

  const lookupOptionsFor = useCallback(
    (columnKey: string): { value: string; label: string }[] => {
      const column = columns.find((c) => c.key === columnKey);
      if (!column) return [];
      if (column.filterOptions) return column.filterOptions;
      if (column.filterType !== 'picklist' || !column.getValue) return [];
      const seen = new Set<string>();
      for (const row of data ?? []) {
        const raw = column.getValue(row);
        if (raw !== null && raw !== undefined && raw !== '') seen.add(String(raw));
      }
      return [...seen].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));
    },
    [columns, data]
  );

  const facetCountsFor = useCallback(
    (columnKey: string): Record<string, number> => {
      const column = columns.find((c) => c.key === columnKey);
      if (!column) return {};
      const base = applyClientFilters(data ?? [], withoutColumn(filters, columnKey), columns, now);
      const counts = tallyLookup(matchesSearch(base), column);
      if (column.filterType !== 'picklist' && column.filterType !== 'boolean') return counts;
      // Every known option WAS counted: one with no rows under the other filters is a computed 0 (shown dimmed,
      // §16.3), never a missing key, which the combobox reads as "not computed" and shows with no count.
      for (const o of lookupOptionsFor(columnKey)) counts[o.value] ??= 0;
      if (column.filterType === 'boolean') { counts.true ??= 0; counts.false ??= 0; }
      return counts;
    },
    [columns, data, filters, now, matchesSearch, lookupOptionsFor]
  );


  return {
    filters,
    setFilters: list.setFilters,
    sort: list.sort,
    setSort: list.setSort,
    search,
    setSearch,
    group: list.group,
    setGroup: list.setGroup,
    activeViewKey,
    selectView,
    clearAll,
    processedData,
    totalCount: data?.length ?? 0,
    filteredCount: processedData.length,
    facetCountsFor,
    lookupOptionsFor,
  };
}
